import {
  Component,
  forwardRef,
  Inject,
  OnDestroy,
  OnInit,
  SecurityContext,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { editorOptions } from 'src/app/constants/editor-options';
import { environment } from 'src/environments/environment';
import { IExtraInfo, Thumbnail } from 'src/app/interfaces/extra-info';
import { IOrganisationImage } from 'src/app/interfaces/organisation-image';
import { CarouselImagesAdd } from 'src/modules/carousel-image/components/add/component';
import { CarouselImageDataSource } from 'src/modules/carousel-image/services/carousel-image-data-source';
import { AddFiles } from 'src/modules/directives/components/add-files/component';
import { ProductInfoDataSource } from '../../services/product-info-data-source';
import moment from 'moment';
import { Subscription as RxSubscription } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
    selector: 'extra-info-edit-dalog',
    templateUrl: 'component.html',
    styleUrls: ['component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => ProductInfoEdit),
        },
    ],
    standalone: false
})
export class ProductInfoEdit implements OnInit, OnDestroy {
  public form: FormGroup;

  public editorOptions = editorOptions;

  public today: Date = moment().startOf('day').toDate();
  public ProductInfo: IExtraInfo;
  public loading: boolean;
  public selectedFiles = [];

  public startDate: string = null;
  public endDate: string = null;
  protected dataSource: ProductInfoDataSource;
  private Subscriptions: RxSubscription[] = [];
  public galleryChecked: boolean;
  public thumbnails: Thumbnail[] = [];
  public companyId: number = null;
  public uploadProgress: number = 0;
  public editingIndex: number | null = null;
  public editedFileName: string = '';
  constructor(
    private dialog: MatDialog,
    protected dialogRef: MatDialogRef<ProductInfoEdit>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    @Inject(MAT_DIALOG_DATA) public DataSource: CarouselImageDataSource,
    @Inject('window') private window: Window,
    private sanitizer: DomSanitizer
  ) {}
  public fileUrl: string = `${environment.leisureUrl}/product-extra-info-file/`;
  public imageUrl: string = `${environment.listingAdminUrl}/listing-admin/`;
  public editFile = false;
  public fileTitle = '';

  ngOnInit() {
    this.ProductInfo = this.data.ProductInfo || {};
    this.dataSource = this.data.dataSource;
    this.companyId = this.dataSource.getConfig().organisationId;

    this.Subscriptions.push(
      this.dataSource.uploadProgress.subscribe((progress) => {
        this.uploadProgress = progress;
      })
    );

    this.Subscriptions.push(
      this.dataSource.loading.subscribe((loading) => {
        this.loading = loading;
      })
    );

    let files: any[] = [];
    let thumbnails: any[] = [];

    try {
      files = Array.isArray(this.ProductInfo.files)
        ? this.ProductInfo.files
        : JSON.parse(this.ProductInfo.files || '[]');

      thumbnails = Array.isArray(this.ProductInfo.thumbnails)
        ? this.ProductInfo.thumbnails
        : JSON.parse(this.ProductInfo.thumbnails || '[]');
    } catch (e) {
      console.warn('Error parsing files or thumbnails:', e);
    }

    this.thumbnails = thumbnails;

    if (files.length > 0) {
      this.selectedFiles = files.map((f: any) => ({
        name: f.file_title || f.name,
        file_title_display: f.file_title_display || null,
        type: f.file_type,
        downloadUrl: f.downloadUrl,
        editing: false,
      }));

      this.galleryChecked = false;
    } else if (thumbnails.length > 0) {
      this.selectedFiles = [];
      this.showGallery(true);
    } else {
      this.selectedFiles = [];
      this.galleryChecked = false;
    }

    const formBuilder = new FormBuilder();

    this.form = formBuilder.group({
      title: [null, [Validators.required]],
      description: [null, [Validators.required]],
      start_date: [null],
      type: ['product', [Validators.required]],
      end_date: [null],
      files: formBuilder.array([]),
      file_title: [null],
      thumbnails: [null],
    });

    const filesFormArray = this.form.get('files') as FormArray;
    this.selectedFiles.forEach((file) => {
      filesFormArray.push(
        formBuilder.group({
          name: [file.name, Validators.required],
          originalName: [file.originalName],
          downloadUrl: [file.downloadUrl],
          type: [file.type],
        })
      );
    });

    if (this.ProductInfo) {
      Object.keys(this.ProductInfo).forEach((name) => {
        if (this.form.controls[name]) {
          this.form.controls[name].patchValue(this.ProductInfo[name]);
        }
      });

      this.startDate = this.ProductInfo.start_date;
      this.endDate = this.ProductInfo.end_date;

      if (!!this.ProductInfo.thumbnails) {
        this.thumbnails = this.ProductInfo.thumbnails as Thumbnail[];
      }
      if (!!this.thumbnails?.length) {
        this.showGallery(!!this.thumbnails);
      }
    }
  }

  disableEdit(index: number) {
    const file = this.selectedFiles[index];
    const newTitle = file.tempTitle?.trim();

    if (newTitle && newTitle !== file.file_title_display) {
      file.file_title_display = newTitle;
    } else if (newTitle === file.name) {
      file.file_title_display = null;
    }

    file.editing = false;
  }

  onSubmit() {
    this.form.disable();

    const form = this.form.getRawValue();
    const description = form.description.replace(/<p><br><\/p>/g, '');

    const updatedFiles = this.selectedFiles.map((file) => {
      const originalName = file.name?.trim();
      const editedName = file.tempTitle?.trim();

      let file_title_display = file.file_title_display ?? null;

      if (editedName && editedName !== originalName) {
        file_title_display = editedName;
      }

      return {
        ...file,
        file_title_display,
        type: file.type || null,
      };
    });

    const update = {
      ...this.ProductInfo,
      ...form,
      description,
      files: updatedFiles,
    };

    this.dataSource
      .save(update)
      .then(() => this.dialogRef.close())
      .catch((error) => console.log(error));
  }

  ngOnDestroy() {
    this.Subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public setType(type: string) {
    this.form.controls['type'].patchValue(type);
  }

  public addFile() {
    const config = new MatDialogConfig();
    config.disableClose = true;
    config.data = { allowMultpleUpload: true };

    this.dialog
      .open(AddFiles, config)
      .afterClosed()
      .subscribe(({ files }) => {
        if (files && files.length) {
          this.selectedFiles = [
            ...this.selectedFiles,
            ...files.map((f) => ({
              ...f,
              src: f.blob || f.file || f.src || f,
            })),
          ];

          this.form.patchValue({
            files: this.selectedFiles,
          });
        }
      });
  }

  public enableEdit(index: number) {
    const file = this.selectedFiles[index];
    file.editing = true;
    file.tempTitle = file.file_title_display || file.name || '';
  }

  public previewFileByIndex(index: number) {
    const file = this.selectedFiles[index];
    if (!file) return;

    let src: string | null = null;
    if ('downloadUrl' in file && file.downloadUrl) {
      src = file.downloadUrl;
    } else if ('blobUrl' in file && file.blobUrl) {
      src = this.sanitizer.sanitize(SecurityContext.URL, file.blobUrl);
    } else {
      src = `${this.fileUrl}${this.ProductInfo.id}/${file.name}`;
    }

    if (src) {
      this.window.open(src);
    } else {
      console.warn('No preview URL available for file:', file);
    }
  }

  public removeFile(index?: number) {
    if (typeof index === 'number') {
      const file = this.selectedFiles[index];
      if (!file || !confirm(`Remove file: ${file.name}?`)) {
        return;
      }

      this.dataSource
        .deleteFile(this.ProductInfo.id, file.name)
        .then(() => {
          this.selectedFiles.splice(index, 1);
          this.form.patchValue({ files: this.selectedFiles });

          if (this.selectedFiles.length === 0) {
            this.form.patchValue({ file_title: null });
            this.fileTitle = '';
          }
        })
        .catch((err) => {
          console.error('Error deleting file:', err);
          alert('Failed to delete file.');
        });
    } else {
      const fileTitle = this.form.controls['file_title']?.value;
      if (!fileTitle || !confirm(`Remove ImageFile: ${fileTitle}?`)) {
        return;
      }

      this.dataSource
        .deleteFile(this.ProductInfo.id, fileTitle)
        .then(() => {
          this.form.patchValue({
            file: null,
            file_title: null,
          });
        })
        .catch((err) => {
          console.error('Error deleting file:', err);
          alert('Failed to delete file.');
        });
    }
  }

 public showGallery(fixed: boolean) {
  this.galleryChecked = fixed;

  if (!fixed) {
    this.thumbnails = [];
    this.form.controls['thumbnails'].patchValue([]);
  }
}


  public removeGalleryFile(selectedThumbnail) {
    this.thumbnails = this.thumbnails.filter((thumbnailObj) => {
      return thumbnailObj.id !== selectedThumbnail.id;
    });
    const { thumbnails } = this;
    this.form.controls['thumbnails'].patchValue(thumbnails);
  }

  public addNewImage(thumbnails) {
    const config = new MatDialogConfig();
    const thumbnailsArr = [];
    thumbnails.forEach((thumbnail) => thumbnailsArr.push(thumbnail.id));
    config.data = {
      role: this.dataSource.getConfig().role,
      existingThumbnails: thumbnailsArr,
      organisationId: this.dataSource.getConfig().organisationId,
      Carousel: [],
    };

    this.Subscriptions.push(
      this.dialog
        .open(CarouselImagesAdd, config)
        .afterClosed()
        .subscribe((CarouselImage: IOrganisationImage) => {
          for (const key in CarouselImage) {
            const { id: company_image_id } = CarouselImage[key];
            this.thumbnails.push({ company_image_id, ...CarouselImage[key] });
            const { thumbnails } = this;
            this.form.controls['thumbnails'].patchValue(thumbnails);
          }
        })
    );
  }

  public toggleEdit(index: number): void {
    this.editingIndex = this.editingIndex === index ? null : index;
  }

  public getFileExtension() {
    const title = this.form.controls['file_title'].value;
    const fileExtention = title.split('.').pop();
    return `.${fileExtention}`;
  }

  public getImageUrl(image) {
    const fileExtenstion = image.file_name.split('.').pop();
    return `${environment.cloudFrontDomainName}/${this.companyId}/${image.company_image_id}/thumbnail.${fileExtenstion}`;
  }
}
